import { AlertTriangle, CheckCircle2, Info, OctagonAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { type Tone, toneClasses } from '../lib/tokens';
import { useUi } from '../lib/ui-provider';

export function Spinner(props: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={cn('bl-spin size-5 text-current', props.className)}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

const ALERT_ICONS: Record<Tone, ReactNode> = {
  neutral: <Info className="size-4.5" />,
  primary: <Info className="size-4.5" />,
  info: <Info className="size-4.5" />,
  success: <CheckCircle2 className="size-4.5" />,
  warning: <AlertTriangle className="size-4.5" />,
  danger: <OctagonAlert className="size-4.5" />,
};

/** Inline message. `danger` alerts are announced immediately (role=alert). */
export function Alert(props: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const tone = props.tone ?? 'info';
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-3 rounded-md px-4 py-3 text-sm',
        toneClasses[tone],
        props.className,
      )}
    >
      <span className="mt-0.5 shrink-0" aria-hidden="true">
        {ALERT_ICONS[tone]}
      </span>
      <div className="min-w-0 flex-1">
        {props.title ? <p className="font-medium">{props.title}</p> : null}
        {props.children ? <div className="text-ink-muted">{props.children}</div> : null}
      </div>
      {props.action ? <div className="shrink-0">{props.action}</div> : null}
    </div>
  );
}

export function EmptyState(props: {
  icon?: ReactNode;
  title: ReactNode;
  /** Use `h1` when the state is the whole page (404, 403). */
  titleAs?: 'h1' | 'h2' | 'p';
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-12 text-center', props.className)}>
      {props.icon ? (
        <span
          className="mb-4 flex size-12 items-center justify-center rounded-full bg-surface-muted text-ink-subtle"
          aria-hidden="true"
        >
          {props.icon}
        </span>
      ) : null}
      <Title as={props.titleAs}>{props.title}</Title>
      {props.description ? (
        <p className="mt-1 max-w-sm text-sm text-ink-muted">{props.description}</p>
      ) : null}
      {props.action ? <div className="mt-5">{props.action}</div> : null}
    </div>
  );
}

function Title(props: { as?: 'h1' | 'h2' | 'p' | undefined; children: ReactNode }) {
  const Tag = props.as ?? 'p';
  return <Tag className="text-base font-semibold text-ink">{props.children}</Tag>;
}

export function ErrorState(props: {
  title: ReactNode;
  titleAs?: 'h1' | 'h2' | 'p';
  description?: ReactNode;
  onRetry?: () => void;
  action?: ReactNode;
  className?: string;
}) {
  const { labels } = useUi();
  return (
    <div
      role="alert"
      className={cn('flex flex-col items-center px-6 py-12 text-center', props.className)}
    >
      <span
        className="mb-4 flex size-12 items-center justify-center rounded-full bg-danger-soft text-danger"
        aria-hidden="true"
      >
        <OctagonAlert className="size-5" />
      </span>
      <Title as={props.titleAs}>{props.title}</Title>
      {props.description ? (
        <div className="mt-1 max-w-sm text-sm text-ink-muted">{props.description}</div>
      ) : null}
      <div className="mt-5 flex gap-2">
        {props.onRetry ? (
          <button
            type="button"
            onClick={props.onRetry}
            className="inline-flex h-9 items-center rounded-md border border-line-strong bg-surface px-3 text-sm font-medium text-ink hover:bg-surface-muted"
          >
            {labels.retry}
          </button>
        ) : null}
        {props.action}
      </div>
    </div>
  );
}

/** Inline loading for a region; never a full-screen blocker. */
export function LoadingState(props: { label?: string; className?: string }) {
  const { labels } = useUi();
  return (
    <div
      role="status"
      className={cn(
        'flex items-center justify-center gap-2 py-12 text-sm text-ink-muted',
        props.className,
      )}
    >
      <Spinner className="size-4" />
      <span>{props.label ?? labels.loading}</span>
    </div>
  );
}
